'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import { usePathname, useRouter } from '@/i18n/routing';
import { useTranslations, useLocale } from 'next-intl';
import { useDashboardMode } from '@/context/DashboardContext';
import { useAuthStore } from '@/stores/auth';
import { 
  User, 
  Ship,
  ChevronDown,
  Check,
  Shield,
  AlertTriangle,
  Plus
} from 'lucide-react';

type RoleSwitcherProps = {
  /** When 'left', dropdown aligns to left edge of button (use in side drawer so it doesn't overflow left). Default 'right'. */
  alignDropdown?: 'left' | 'right';
};

export function RoleSwitcher({ alignDropdown = 'right' }: RoleSwitcherProps) {
  const t = useTranslations();
  const _locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const { activeContext, setPersonalContext, setOrganizerContext, setAdminContext, isPersonalContext, isAdminContext } = useDashboardMode();
  const authStore = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [organizerCruiseCounts, setOrganizerCruiseCounts] = useState<Record<string, number>>({});
  const [warningDialog, setWarningDialog] = useState<{ show: boolean; targetType: 'PERSONAL' | 'ORGANIZER' | 'ADMIN' | null; targetId?: string; targetName?: string }>({ show: false, targetType: null });
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get user's organizations (memberships)
  const userOrganizations = useMemo(() => authStore.user?.memberships || [], [authStore.user?.memberships]);

  // Fetch cruise counts for organizers (optional - can be enhanced later)
  useEffect(() => {
    if (userOrganizations.length > 0 && isOpen) {
      // TODO: Fetch actual cruise counts from API
      // For now, we'll assume all organizers might have cruises
      // This can be enhanced to fetch real data from /api/v1/organizations/{id}/cruises/count
      const counts: Record<string, number> = {};
      userOrganizations.forEach((membership) => {
        // Placeholder: assume they have cruises if we want to show the badge
        // In production, fetch this from the API
        counts[membership.organization.id] = 0; // Will be updated when API is available
      });
      setOrganizerCruiseCounts(counts);
    }
  }, [userOrganizations, isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Check if wizard has saved cruise with organization
  const checkWizardConflict = (targetType: 'PERSONAL' | 'ORGANIZER' | 'ADMIN', targetId?: string): boolean => {
    // Only check if we're on the cruise wizard page
    if (!pathname?.includes('/cruises/create')) {
      return false;
    }

    // Check if we're editing a cruise (has ?id= parameter)
    const urlParams = new URLSearchParams(window.location.search);
    const cruiseId = urlParams.get('id');
    
    // If editing a cruise, block ACT changes that would change the organization
    if (cruiseId) {
      // Get current organization from authStore or check if it's private
      const currentOrgId = authStore.activeOrganizationId;
      
      // If switching to personal but cruise has organization
      if (targetType === 'PERSONAL' && currentOrgId) {
        return true;
      }
      // If switching to different organizer
      if (targetType === 'ORGANIZER' && targetId && currentOrgId && currentOrgId !== targetId) {
        return true;
      }
      // If switching to organizer but cruise is private (no organization)
      if (targetType === 'ORGANIZER' && targetId && !currentOrgId) {
        return true;
      }
      // If switching to admin (should also be blocked when editing)
      if (targetType === 'ADMIN') {
        return true;
      }
      // Allow if switching to the same organizer (no change)
      // This case returns false (no conflict)
    }
    
    // Check localStorage for saved cruise data (for new cruises)
    try {
      // Check for new cruise draft
      const newDraft = localStorage.getItem('cruise_draft_new');
      if (newDraft) {
        try {
          const draftData = JSON.parse(newDraft);
          const draftOrgId = draftData.data?.organization_id;
          
          // Only block if draft has meaningful data (not just empty form)
          // Check if draft has at least some basic data (name or dates)
          const hasData = draftData.data?.name || draftData.data?.embarkation_date || draftData.data?.embarkation_place;
          
          if (hasData) {
            // If switching to personal but draft has organization
            if (targetType === 'PERSONAL' && draftOrgId) {
              return true;
            }
            // If switching to different organizer
            if (targetType === 'ORGANIZER' && targetId && draftOrgId && draftOrgId !== targetId) {
              return true;
            }
            // If switching to organizer but draft is private (no organization)
            if (targetType === 'ORGANIZER' && targetId && !draftOrgId) {
              return true;
            }
          }
        } catch (_e) {
          // Skip invalid JSON
        }
      }

      // Check for edit cruise drafts (format: cruise_draft_edit_{id})
      const editDraftKeys = Object.keys(localStorage).filter(key => key.startsWith('cruise_draft_edit_'));
      for (const key of editDraftKeys) {
        const draft = localStorage.getItem(key);
        if (draft) {
          try {
            const draftData = JSON.parse(draft);
            const draftOrgId = draftData.data?.organization_id;
            
            // For edit drafts, always check (they represent saved cruises)
            // If switching to personal but draft has organization
            if (targetType === 'PERSONAL' && draftOrgId) {
              return true;
            }
            // If switching to different organizer
            if (targetType === 'ORGANIZER' && targetId && draftOrgId && draftOrgId !== targetId) {
              return true;
            }
            // If switching to organizer but draft is private (no organization)
            if (targetType === 'ORGANIZER' && targetId && !draftOrgId) {
              return true;
            }
          } catch (_e) {
            // Skip invalid JSON
          }
        }
      }
    } catch (error) {
      console.error('Error checking wizard conflict:', error);
    }

    return false;
  };

  // Handle context change
  const handleContextChange = (type: 'PERSONAL' | 'ORGANIZER' | 'ADMIN', id?: string, name?: string) => {
    // Check for conflicts if on wizard page
    if (checkWizardConflict(type, id)) {
      setWarningDialog({ show: true, targetType: type, targetId: id, targetName: name });
      return;
    }

    // Proceed with context change
    if (type === 'PERSONAL') {
      setPersonalContext();
      // Sync with authStore for cruise wizard
      authStore.setActiveOrganization(null);
    } else if (type === 'ORGANIZER' && id) {
      setOrganizerContext(id, name);
      // Sync with authStore for cruise wizard
      authStore.setActiveOrganization(id);
    } else if (type === 'ADMIN') {
      setAdminContext();
      // Keep current organization in authStore (admin doesn't change it)
      // When switching to Admin on a cruise page (detail or create), redirect to admin dashboard
      if (pathname?.includes('/cruises/')) {
        router.push('/dashboard');
      }
    }
    setIsOpen(false);
  };

  // Handle warning dialog confirm (only for new cruises, not editing)
  const handleWarningConfirm = () => {
    if (!warningDialog.targetType) return;

    // Check if we're editing - if so, don't allow continuing
    const urlParams = new URLSearchParams(window.location.search);
    const cruiseId = urlParams.get('id');
    if (cruiseId) {
      // When editing, just close the dialog - don't allow context change
      setWarningDialog({ show: false, targetType: null });
      setIsOpen(false);
      return;
    }

    const { targetType, targetId, targetName } = warningDialog;
    
    if (targetType === 'PERSONAL') {
      setPersonalContext();
      authStore.setActiveOrganization(null);
    } else if (targetType === 'ORGANIZER' && targetId) {
      setOrganizerContext(targetId, targetName);
      authStore.setActiveOrganization(targetId);
    } else if (targetType === 'ADMIN') {
      setAdminContext();
    }
    
    setWarningDialog({ show: false, targetType: null });
    setIsOpen(false);
  };

  // Handle warning dialog cancel
  const handleWarningCancel = () => {
    setWarningDialog({ show: false, targetType: null });
  };

  // Get current context display
  const getCurrentLabel = () => {
    if (isAdminContext) {
      return t('roleSwitcher.admin') || 'Admin';
    }
    if (isPersonalContext) {
      return t('roleSwitcher.personal') || 'Personal';
    }
    if (activeContext.type === 'ORGANIZER' && activeContext.name) {
      return activeContext.name;
    }
    if (activeContext.type === 'ORGANIZER' && activeContext.id) {
      // Try to find name from memberships
      const org = userOrganizations.find(m => m.organization.id === activeContext.id);
      return org?.organization.name || t('roleSwitcher.organizer') || 'Organizer';
    }
    return t('roleSwitcher.personal') || 'Personal';
  };

  const CurrentIcon = isAdminContext ? Shield : (isPersonalContext ? User : Ship);
  
  // Check if user is master admin
  const isMasterAdmin = authStore.user?.is_master_admin === true;

  // Don't show if user is not logged in
  if (!authStore.isLoggedIn) {
    return null;
  }

  return (
    <div className={`relative ${alignDropdown === 'left' ? 'w-full min-w-0' : 'w-fit'}`} ref={dropdownRef}>
      {/* Role Switcher Button - Ghost Style */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-all duration-200 hover:text-slate-900 dark:hover:text-slate-100 h-10 min-w-0 overflow-hidden ${alignDropdown === 'left' ? 'w-full max-w-full' : 'w-[140px] xl:w-[160px]'}`}
        aria-label={t('roleSwitcher.ariaLabel') || 'Switch dashboard context'}
        aria-expanded={isOpen}
      >
        <CurrentIcon size={20} className="flex-shrink-0" />
        <span className={`text-sm font-medium whitespace-nowrap truncate flex-1 min-w-0 text-left overflow-hidden ${alignDropdown === 'left' ? 'inline' : 'hidden xl:inline'}`}>
          {getCurrentLabel()}
        </span>
        <ChevronDown 
          size={16} 
          className={`flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu with Animation */}
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div 
            className={`absolute mt-2 w-64 max-h-[calc(100vh-200px)] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden ${alignDropdown === 'left' ? 'left-0 right-auto' : 'right-0 left-auto'}`}
            style={{
              animation: 'slideDown 0.2s ease-out',
            }}
          >
            {/* Section Header */}
            <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 flex-shrink-0">
              <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                {t('roleSwitcher.actAs') || 'Act as:'}
              </div>
            </div>

            {/* Role list - scrollable */}
            <div className="p-2 overflow-y-auto flex-1 min-h-0">
              <button
                onClick={() => handleContextChange('PERSONAL')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-150 ${
                  isPersonalContext
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                }`}
              >
                <User 
                  size={20} 
                  className={`flex-shrink-0 ${
                    isPersonalContext 
                      ? 'text-blue-600 dark:text-blue-400' 
                      : 'text-slate-500 dark:text-slate-400'
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">
                    {t('roleSwitcher.personal') || 'Personal'}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {t('roleSwitcher.personalDescription') || 'Your favorites, cruises, and trips'}
                  </div>
                </div>
                {isPersonalContext && (
                  <Check 
                    size={18} 
                    className="flex-shrink-0 text-blue-600 dark:text-blue-400"
                  />
                )}
              </button>

              {/* Admin Option - Only show for master admins */}
              {isMasterAdmin && (
                <button
                  onClick={() => handleContextChange('ADMIN')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-150 mt-1 ${
                    isAdminContext
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                  }`}
                >
                  <Shield 
                    size={20} 
                    className={`flex-shrink-0 ${
                      isAdminContext 
                        ? 'text-blue-600 dark:text-blue-400' 
                        : 'text-slate-500 dark:text-slate-400'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">
                      {t('roleSwitcher.admin') || 'Admin'}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {t('roleSwitcher.adminDescription') || 'Approvals and admin functions'}
                    </div>
                  </div>
                  {isAdminContext && (
                    <Check 
                      size={18} 
                      className="flex-shrink-0 text-blue-600 dark:text-blue-400"
                    />
                  )}
                </button>
              )}

              {/* Organizers Section */}
              {userOrganizations.length > 0 && (
                <div className="mt-2 space-y-1">
                  {userOrganizations.map((membership) => {
                    const isSelected = activeContext.type === 'ORGANIZER' && activeContext.id === membership.organization.id;
                    const hasActiveCruises = organizerCruiseCounts[membership.organization.id] > 0;
                    
                    return (
                      <button
                        key={membership.organization.id}
                        onClick={() => handleContextChange('ORGANIZER', membership.organization.id, membership.organization.name)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-150 relative ${
                          isSelected
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                            : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                        }`}
                      >
                        <div className="relative flex-shrink-0">
                          <Ship 
                            size={20} 
                            className={`${
                              isSelected 
                                ? 'text-blue-600 dark:text-blue-400' 
                                : 'text-slate-500 dark:text-slate-400'
                            }`}
                          />
                          {/* Active cruises badge */}
                          {hasActiveCruises && (
                            <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border-2 border-white dark:border-slate-900" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">
                            {membership.organization.name}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                            {t('roleSwitcher.organizerDescription') || 'Organization cruises and settings'}
                          </div>
                        </div>
                        {isSelected && (
                          <Check 
                            size={18} 
                            className="flex-shrink-0 text-blue-600 dark:text-blue-400"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Create new organizer */}
              <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-800">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    router.push('/organizations/create');
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-150 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300"
                >
                  <Plus
                    size={20}
                    className="flex-shrink-0 text-emerald-600 dark:text-emerald-400"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">
                      {t('userMenu.createOrganization') || 'Create organizer'}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                      {t('userMenu.createOrganizationSubtitle') || 'Register a new organizer'}
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Warning Dialog */}
      {warningDialog.show && (
        <>
          <div 
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" 
            onClick={handleWarningCancel}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-md w-full border border-slate-200 dark:border-slate-800">
              <div className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex-shrink-0">
                    <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                      {t('roleSwitcher.warning.title') || 'Cannot Change Context'}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {(() => {
                        const urlParams = new URLSearchParams(window.location.search);
                        const cruiseId = urlParams.get('id');
                        if (cruiseId) {
                          return t('roleSwitcher.warning.editMessage') || 'You cannot change the organization context while editing a cruise. The cruise owner cannot be changed after creation.';
                        }
                        return t('roleSwitcher.warning.message') || 'You have a saved cruise with a different organization type. Please finish or cancel the current cruise wizard before changing the context.';
                      })()}
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  {(() => {
                    const urlParams = new URLSearchParams(window.location.search);
                    const cruiseId = urlParams.get('id');
                    const isEditing = !!cruiseId;
                    
                    if (isEditing) {
                      // When editing, only show "OK" button (no continue option)
                      return (
                        <button
                          onClick={handleWarningCancel}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          {t('common.ok') || 'OK'}
                        </button>
                      );
                    }
                    
                    // For new cruises, show both Cancel and Continue options
                    return (
                      <>
                        <button
                          onClick={handleWarningCancel}
                          className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                        >
                          {t('common.cancel') || 'Cancel'}
                        </button>
                        <button
                          onClick={handleWarningConfirm}
                          className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                        >
                          {t('roleSwitcher.warning.continue') || 'Continue Anyway'}
                        </button>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
